<template>
    <div class="limiter-content pa-3 pa-lg-0">
        <div class="padding-block"></div>

        <!-- Cabecalho no padrao das telas da area do credor/procurador -->
        <header class="ma-header">
            <span class="ma-eyebrow">
                <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
                {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
            </span>
            <h1 class="ma-title">Minha <span>conta</span></h1>
            <p class="ma-subtitle">Seus dados de cadastro, endereço e documentos enviados à plataforma.</p>
        </header>

        <login-required-notice v-if="!$root.user" />

        <template v-else>
        <!-- Resumo da conta: dados que nao sao editaveis aqui -->
        <section class="ma-summary">
            <div class="ma-avatar" :class="isPj ? 'ma-avatar--pj' : 'ma-avatar--pf'">{{ initials }}</div>
            <div class="ma-summary__main">
                <div class="ma-summary__name">{{ dataset.user.name || "—" }}</div>
                <div class="ma-summary__email">{{ dataset.user.email }}</div>
            </div>
            <dl class="ma-facts">
                <div>
                    <dt>{{ isPj ? "CNPJ" : "CPF" }}</dt>
                    <dd>{{ dataset.user.registration_formatted || "—" }}</dd>
                </div>
                <div v-if="personTypeLabel">
                    <dt>Tipo de pessoa</dt>
                    <dd>{{ personTypeLabel }}</dd>
                </div>
                <div v-if="isProcurator">
                    <dt>OAB</dt>
                    <dd>{{ dataset.user.oab ? `${dataset.user.oab}${dataset.user.oab_state ? "/" + dataset.user.oab_state : ""}` : "Não informada" }}</dd>
                </div>
            </dl>
        </section>

        <!-- Avisos -->
        <div v-if="profile.update_requested" class="ma-notice ma-notice--warning">
            <v-icon color="#a16207" class="ma-notice__icon">mdi-alert-circle-outline</v-icon>
            <div>
                <strong>O Administrador Judicial solicitou a atualização do seu cadastro.</strong>
                <div v-if="profile.update_reason" class="mt-1">Motivo: {{ profile.update_reason }}</div>
                <div v-if="profile.missing.length" class="ma-missing">
                    <span v-for="item in profile.missing" :key="item.key" class="ma-missing__item">{{ item.label }}</span>
                </div>
            </div>
        </div>
        <div v-else-if="!isProcurator && profile.missing.length" class="ma-notice">
            <v-icon color="darkSecondary" class="ma-notice__icon">mdi-information-outline</v-icon>
            <div>
                <strong>Complete seu cadastro</strong> para poder aderir a planos de recuperação judicial pela plataforma.
                <div class="ma-missing">
                    <span v-for="item in profile.missing" :key="item.key" class="ma-missing__item">{{ item.label }}</span>
                </div>
            </div>
        </div>

        <v-form ref="form" v-model="dataForm.validForm" class="ma-form">
            <section class="ma-card">
                <div class="ma-card__head">
                    <h2>Dados pessoais</h2>
                    <p>Nome e telefone de contato. E-mail e {{ isPj ? "CNPJ" : "CPF" }} não podem ser alterados por aqui.</p>
                </div>
                <v-row dense>
                    <v-col cols="12" sm="7">
                        <v-text-field dense label="Nome completo" v-model="dataset.user.name" required
                            :rules="[$rules.required]" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="5">
                        <v-text-field dense label="Telefone" v-model="dataset.user.phone_number"
                            outlined hide-details="auto"></v-text-field>
                    </v-col>
                </v-row>
            </section>

            <section class="ma-card">
                <div class="ma-card__head">
                    <h2>Endereço</h2>
                    <p>Usado nos termos de adesão a planos de recuperação judicial.</p>
                </div>
                <v-row dense>
                    <v-col cols="12" sm="3">
                        <v-text-field dense label="CEP" v-model="dataset.user.zip_code" v-mask="'#####-###'"
                            :rules="[rules.zipCode]" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="7">
                        <v-text-field dense label="Logradouro" v-model="dataset.user.street"
                            maxlength="255" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-text-field dense label="Número" v-model="dataset.user.address_number"
                            maxlength="20" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field dense label="Complemento" v-model="dataset.user.address_complement"
                            maxlength="120" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field dense label="Bairro" v-model="dataset.user.district"
                            maxlength="120" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field dense label="Cidade" v-model="dataset.user.city"
                            maxlength="120" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="2">
                        <v-select dense label="UF" v-model="dataset.user.state" :items="ufs" clearable
                            outlined hide-details="auto"></v-select>
                    </v-col>
                </v-row>
            </section>

            <section v-if="isPj" class="ma-card">
                <div class="ma-card__head">
                    <h2>Representante legal</h2>
                    <p>Quem assina pela empresa. O CPF é conferido com a assinatura digital do termo de adesão.</p>
                </div>
                <v-row dense>
                    <v-col cols="12" sm="5">
                        <v-text-field dense label="Nome completo" v-model="dataset.user.legal_rep_name"
                            maxlength="255" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="3">
                        <v-text-field dense label="CPF" v-model="dataset.user.legal_rep_registration" v-mask="'###.###.###-##'"
                            :rules="[rules.cpf]" outlined hide-details="auto"></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="4">
                        <v-text-field dense label="Cargo" v-model="dataset.user.legal_rep_role" placeholder="Ex.: Sócio-administrador"
                            maxlength="120" outlined hide-details="auto"></v-text-field>
                    </v-col>
                </v-row>
            </section>

            <div class="ma-save">
                <v-btn id="btnSave" type="button" depressed color="primary" class="ma-btn" :loading="dataForm.loading" @click="save">
                    Salvar alterações
                </v-btn>
            </div>
        </v-form>

        <section v-if="isPj" class="ma-card">
            <div class="ma-card__head">
                <h2>Documentos societários</h2>
                <p>
                    Necessários para aderir a planos de recuperação judicial em nome da empresa. Cada documento é analisado pelo
                    Administrador Judicial, e a adesão só é liberada com os três aprovados. PDF ou imagem, até 10 MB.
                </p>
            </div>
            <article v-for="docType in companyDocTypes" :key="docType.id" class="ma-doc">
                <div class="ma-doc__icon" :class="`ma-doc__icon--${companyDocState(docType.id).key}`">
                    <v-icon small>{{ companyDocState(docType.id).icon }}</v-icon>
                </div>
                <div class="ma-doc__info">
                    <div class="ma-doc__name">{{ docType.name }}</div>
                    <div class="ma-doc__meta">
                        <template v-if="companyDoc(docType.id)">
                            <a v-if="companyDoc(docType.id).file_url" :href="companyDoc(docType.id).file_url" target="_blank" rel="noopener">{{ companyDoc(docType.id).name }}</a>
                            <span v-else>{{ companyDoc(docType.id).name }}</span>
                        </template>
                        <span v-else>Não enviado</span>
                    </div>
                    <div v-if="companyDocState(docType.id).reason" class="ma-doc__reason">
                        <v-icon x-small color="error">mdi-alert-circle-outline</v-icon> {{ companyDocState(docType.id).reason }}
                    </div>
                </div>
                <span v-if="companyDoc(docType.id)" class="ma-status" :class="`ma-status--${companyDocState(docType.id).key}`">
                    <span class="ma-status__dot"></span>{{ companyDocState(docType.id).label }}
                </span>
                <v-btn text rounded small color="darkSecondary" class="ma-action" :loading="uploadingDocType === docType.id"
                    @click="pickCompanyDoc(docType.id)">
                    <v-icon left small>{{ companyDoc(docType.id) ? "mdi-swap-horizontal" : "mdi-upload" }}</v-icon>
                    {{ companyDoc(docType.id) ? "Substituir" : "Enviar" }}
                </v-btn>
            </article>
            <input ref="companyDocInput" type="file" accept=".pdf,.png,.jpg,.jpeg" class="d-none" @change="uploadCompanyDoc">
        </section>

        <section v-if="dataset.documents.length > 0" class="ma-card">
            <div class="ma-card__head">
                <h2>Documentos</h2>
                <p>Enviados no seu cadastro na plataforma.</p>
            </div>
            <article v-for="(doc, index) in dataset.documents" :key="index" class="ma-doc">
                <div class="ma-doc__icon ma-doc__icon--neutral"><v-icon small>mdi-card-account-details-outline</v-icon></div>
                <div class="ma-doc__info">
                    <div class="ma-doc__name">{{ doc.name }}</div>
                </div>
                <v-btn text rounded small color="darkSecondary" class="ma-action" @click="openDoc(doc.url)">
                    <v-icon left small>mdi-open-in-new</v-icon> Ver documento
                </v-btn>
            </article>
        </section>
        </template>
    </div>
</template>


<script>
import axios from "axios";
import { cpf, cnpj } from 'cpf-cnpj-validator';

const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"];
// Tipos de user_docs do cadastro PJ (UserDocTypeEnum no backend).
const COMPANY_DOC_TYPES = [
    { id: 11, name: "Contrato/Estatuto social" },
    { id: 12, name: "Ata ou documento de eleição do representante" },
    { id: 13, name: "Documento do representante legal" },
];
const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

export default {
    name: "MyAccount",

    data: () => ({
        dataForm: {
            validForm: true,
            loading: false,
        },
        dataset: {
            user: {
                name: "",
                email: "",
                phone_number: "",
                registration: "",
                registration_formatted: "",
                type: null,
                oab: "",
                oab_state: "",
                person_type: null,
                zip_code: "",
                street: "",
                address_number: "",
                address_complement: "",
                district: "",
                city: "",
                state: null,
                legal_rep_name: "",
                legal_rep_registration: "",
                legal_rep_role: ""
            },
            documents: []
        },
        profile: {
            missing: [],
            update_requested: false,
            update_reason: null
        },
        companyDocs: [],
        uploadingDocType: null,
        pendingDocType: null,
        ufs: UFS,
        companyDocTypes: COMPANY_DOC_TYPES,
        rules: {
            zipCode: (value) => !value || onlyDigits(value).length === 8 || "CEP deve ter 8 dígitos",
            cpf: (value) => !value || cpf.isValid(onlyDigits(value)) || "CPF inválido"
        }
    }),

    computed: {
        isProcurator() {
            return Number(this.dataset.user.type) === 2;
        },
        isPj() {
            return this.dataset.user.person_type === "pj";
        },
        initials() {
            const words = String(this.dataset.user.name || "").trim().split(/\s+/).filter((word) => word.length > 2);
            return (((words[0] || "")[0] || "?") + ((words[1] || "")[0] || "")).toUpperCase();
        },
        personTypeLabel() {
            if (this.dataset.user.person_type === "pj") return "Pessoa jurídica";
            if (this.dataset.user.person_type === "pf") return "Pessoa física";
            return "";
        },
    },

    created() {
        if (this.$root.user) this.loadUserInfo();
    },

    methods: {
        formatCpfCnpj(value) {
            if (!value) return "";
            const cleanValue = value.replace(/\D/g, '');
            return cleanValue.length <= 11 ? cpf.format(cleanValue) : cnpj.format(cleanValue);
        },

        async loadUserInfo() {
            try {
                const result = await axios.get('/api/users/info');
                const data = result.data.content;

                this.dataset.user = {
                    name: data.name,
                    email: data.email,
                    phone_number: data.phone_number,
                    registration: data.registration,
                    registration_formatted: this.formatCpfCnpj(data.registration),
                    type: data.type,
                    oab: data.oab || "",
                    oab_state: data.oab_state || "",
                    person_type: data.person_type || (data.profile_completeness && data.profile_completeness.person_type) || null,
                    zip_code: data.zip_code || "",
                    street: data.street || "",
                    address_number: data.address_number || "",
                    address_complement: data.address_complement || "",
                    district: data.district || "",
                    city: data.city || "",
                    state: data.state || null,
                    legal_rep_name: data.legal_rep_name || "",
                    legal_rep_registration: data.legal_rep_registration || "",
                    legal_rep_role: data.legal_rep_role || ""
                };

                this.applyProfileStatus(data.profile_completeness);
                if (this.isPj) await this.loadCompanyDocs();

                if (data.documentUrl) {
                    this.dataset.documents = [{
                        id: data.document,
                        name: "Documento de Identificação",
                        url: data.documentUrl
                    }];
                }

            } catch (error) {
                this.$dialog.notify.error("Erro ao carregar dados do usuário", { position: 'top-right', timeout: 3000 });
            }
        },

        applyProfileStatus(status) {
            this.profile = {
                missing: (status && status.missing) || [],
                update_requested: !!(status && status.update_requested),
                update_reason: (status && status.update_reason) || null
            };
        },

        async refreshProfileStatus() {
            try {
                const result = await axios.get('/api/users/profile-status');
                this.applyProfileStatus(result.data.content);
            } catch (error) {
                console.error("Erro ao atualizar pendências do cadastro", error);
            }
        },

        async loadCompanyDocs() {
            try {
                const response = await axios.get('/api/user-docs', { params: { start: 1, limit: 100000, search: {} } });
                const docs = (response.data && response.data.content && response.data.content.data) || [];
                const typeIds = COMPANY_DOC_TYPES.map((docType) => docType.id);
                this.companyDocs = docs.filter((doc) => typeIds.includes(Number(doc.type)));
            } catch (error) {
                this.$dialog.notify.error("Erro ao carregar documentos societários", { position: 'top-right', timeout: 3000 });
            }
        },

        // Vale o envio mais recente de cada tipo (o que o AJ analisa).
        companyDoc(typeId) {
            return this.companyDocs
                .filter((doc) => Number(doc.type) === typeId)
                .reduce((latest, doc) => (!latest || Number(doc.id) > Number(latest.id) ? doc : latest), null);
        },

        // Situacao na analise do AJ: 0 em analise, 1 aprovado, 2 recusado.
        companyDocState(typeId) {
            const doc = this.companyDoc(typeId);
            if (!doc) return { key: "missing", icon: "mdi-file-upload-outline", label: "" };
            const status = Number(doc.status) || 0;
            if (status === 1) return { key: "approved", icon: "mdi-check", label: "Aprovado" };
            if (status === 2) return { key: "rejected", icon: "mdi-close", label: "Recusado: envie novamente", reason: doc.review_reason };
            return { key: "pending", icon: "mdi-clock-outline", label: "Em análise" };
        },

        pickCompanyDoc(typeId) {
            this.pendingDocType = typeId;
            this.$refs.companyDocInput.value = "";
            this.$refs.companyDocInput.click();
        },

        async uploadCompanyDoc(event) {
            const file = event.target.files && event.target.files[0];
            const typeId = this.pendingDocType;
            if (!file || !typeId) return;

            if (file.size > 10 * 1024 * 1024) {
                this.$dialog.notify.error("O arquivo deve ter no máximo 10 MB", { position: 'top-right', timeout: 3000 });
                return;
            }

            this.uploadingDocType = typeId;
            try {
                const formData = new FormData();
                formData.append('name', file.name);
                formData.append('type', String(typeId));
                formData.append('file', file);
                await axios.post('/api/user-docs', formData);

                this.$dialog.notify.success("Documento enviado com sucesso!", { position: "top-right", timeout: 2000 });
                await this.loadCompanyDocs();
                await this.refreshProfileStatus();
            } catch (error) {
                this.$dialog.notify.error("Erro ao enviar documento", { position: 'top-right', timeout: 3000 });
            } finally {
                this.uploadingDocType = null;
                this.pendingDocType = null;
            }
        },

        openDoc(url) {
            window.open(url, '_blank');
        },

        async save() {
            if (!this.$refs.form.validate()) return;

            this.dataForm.loading = true;

            const user = this.dataset.user;
            const payload = {
                name: user.name,
                phone_number: user.phone_number,
                zip_code: onlyDigits(user.zip_code),
                street: user.street,
                address_number: user.address_number,
                address_complement: user.address_complement,
                district: user.district,
                city: user.city,
                state: user.state || ""
            };

            if (this.isPj) {
                payload.legal_rep_name = user.legal_rep_name;
                payload.legal_rep_registration = onlyDigits(user.legal_rep_registration);
                payload.legal_rep_role = user.legal_rep_role;
            }

            try {
                await axios.patch('/api/users/profile', payload);

                this.$dialog.notify.success("Dados atualizados com sucesso!", { position: "top-right", timeout: 2000 });
                
                if(this.$root.user) {
                    this.$root.user.name = payload.name;
                }

                await this.refreshProfileStatus();

            } catch (error) {
                const content = error.response && error.response.data && error.response.data.content;
                const message = (content && content.error && content.error.message) || "Erro ao atualizar dados";
                this.$dialog.notify.error(message, { position: 'top-right', timeout: 3000 });
            } finally {
                this.dataForm.loading = false;
            }
        }
    }
};
</script>

<style scoped>
/* Mesmo padrao visual de "Meus credores": cabecalho leve, cartoes soltos com
   respiro, campos com contorno lilas e status em pilulas suaves. */

.ma-header {
    padding: 32px 0 28px;
}

.ma-eyebrow {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #766386;
}

.ma-title {
    margin: 14px 0 10px;
    font-size: clamp(28px, 3.4vw, 40px);
    font-weight: 700;
    color: #25113c;
    letter-spacing: -0.035em;
    line-height: 1.2;
}

.ma-title span {
    color: #806697;
}

.ma-subtitle {
    margin: 0;
    color: #73677c;
    font-size: 14px;
}

/* Resumo da conta */
.ma-summary {
    display: flex;
    align-items: center;
    gap: 16px 28px;
    flex-wrap: wrap;
    padding: 20px 22px;
    border-radius: 16px;
    background: #faf9fd;
    border: 1px solid rgba(24, 0, 50, 0.06);
    margin-bottom: 20px;
}

.ma-avatar {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    font-weight: 700;
    flex-shrink: 0;
}

.ma-avatar--pf {
    background: rgba(120, 119, 229, 0.16);
    color: #4b3a8f;
}

.ma-avatar--pj {
    background: rgba(170, 211, 199, 0.5);
    color: #1f5b4b;
}

.ma-summary__main {
    min-width: 0;
    flex: 1 1 220px;
}

.ma-summary__name {
    font-size: 17px;
    font-weight: 600;
    color: #180032;
}

.ma-summary__email {
    font-size: 13px;
    color: rgba(24, 0, 50, 0.55);
    word-break: break-all;
}

.ma-facts {
    display: flex;
    gap: 28px;
    flex-wrap: wrap;
    margin: 0;
}

.ma-facts dt {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #8b769d;
}

.ma-facts dd {
    margin: 2px 0 0;
    font-size: 14px;
    font-weight: 500;
    color: #25113c;
}

/* Avisos */
.ma-notice {
    display: flex;
    gap: 12px;
    padding: 14px 18px;
    border-radius: 14px;
    background: rgba(120, 119, 229, 0.09);
    color: #2a1a40;
    font-size: 14px;
    line-height: 1.55;
    margin-bottom: 20px;
}

.ma-notice--warning {
    background: #fff7e6;
}

.ma-notice__icon {
    margin-top: 1px;
}

.ma-missing {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.ma-missing__item {
    padding: 3px 10px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(126, 85, 171, 0.25);
    font-size: 12px;
    color: #4a3a5c;
}

/* Cartoes de secao */
.ma-card {
    padding: 22px 22px 18px;
    border-radius: 16px;
    background: #fff;
    border: 1px solid rgba(24, 0, 50, 0.07);
    margin-bottom: 16px;
}

.ma-card__head {
    margin-bottom: 16px;
}

.ma-card__head h2 {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #7e55ab;
}

.ma-card__head p {
    margin: 4px 0 0;
    font-size: 13px;
    color: rgba(24, 0, 50, 0.55);
}

/* Campos: contorno lilas e cantos de 10px */
.ma-form ::v-deep .v-text-field--outlined fieldset {
    border-color: rgba(126, 85, 171, 0.35);
    border-radius: 10px;
}

.ma-form ::v-deep .v-text-field--outlined:hover fieldset {
    border-color: #7e55ab;
}

.ma-form ::v-deep .v-input__slot {
    border-radius: 10px !important;
}

.ma-save {
    display: flex;
    justify-content: flex-end;
    margin: 4px 0 28px;
}

.ma-btn {
    height: 42px !important;
    padding: 0 24px !important;
    border-radius: 10px;
    text-transform: none;
    letter-spacing: 0;
    font-size: 14px;
    font-weight: 600;
}

/* Linhas de documento */
.ma-doc {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 14px;
    padding: 12px 0;
    border-top: 1px solid rgba(24, 0, 50, 0.06);
}

.ma-doc__icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.ma-doc__icon .v-icon {
    color: inherit;
}

.ma-doc__icon--missing,
.ma-doc__icon--neutral {
    background: rgba(120, 119, 229, 0.12);
    color: #7e55ab;
}

.ma-doc__icon--approved {
    background: #e7f6ee;
    color: #1e7b4f;
}

.ma-doc__icon--pending {
    background: #fff4dc;
    color: #a16207;
}

.ma-doc__icon--rejected {
    background: #fdecea;
    color: #b42318;
}

.ma-doc__name {
    font-size: 14px;
    font-weight: 600;
    color: #180032;
}

.ma-doc__meta {
    margin-top: 2px;
    font-size: 13px;
    color: rgba(24, 0, 50, 0.55);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ma-doc__meta a {
    color: #7e55ab;
    text-decoration: none;
}

.ma-doc__meta a:hover {
    text-decoration: underline;
}

.ma-doc__reason {
    margin-top: 4px;
    font-size: 13px;
    color: #b42318;
}

.ma-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.ma-status__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.ma-status--approved {
    background: #e7f6ee;
    color: #1e7b4f;
}

.ma-status--pending {
    background: #fff4dc;
    color: #a16207;
}

.ma-status--rejected {
    background: #fdecea;
    color: #b42318;
}

.ma-action {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
}

/* Celular */
@media (max-width: 760px) {
    .ma-doc {
        grid-template-columns: auto minmax(0, 1fr);
        gap: 8px 12px;
    }

    .ma-status,
    .ma-action {
        grid-column: 2;
        justify-self: start;
    }

    .ma-save .ma-btn {
        width: 100%;
    }
}
</style>
